import { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import {
  X,
  Buildings,
  EnvelopeSimple,
  MagnifyingGlass,
  CheckCircle,
  Spinner,
  User,
  Phone,
} from '@phosphor-icons/react'
import {
  getAvailableAgents,
  inviteAgent,
  AvailableFreightForwarder,
  InviteAgentRequest,
} from '../../services/shipper-agents'

interface InviteAgentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

type TabType = 'existing' | 'email'

export default function InviteAgentModal({
  isOpen,
  onClose,
  onSuccess,
}: InviteAgentModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('existing')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [availableAgents, setAvailableAgents] = useState<AvailableFreightForwarder[]>([])
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Email invite form
  const [emailForm, setEmailForm] = useState({
    email: '',
    companyName: '',
    contactName: '',
    phone: '',
  })

  // Load available agents when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableAgents()
      // Reset state
      setSelectedAgent(null)
      setSearchTerm('')
      setError(null)
      setEmailForm({
        email: '',
        companyName: '',
        contactName: '',
        phone: '',
      })
    }
  }, [isOpen])

  const loadAvailableAgents = async () => {
    setIsLoading(true)
    try {
      const agents = await getAvailableAgents()
      setAvailableAgents(agents)
    } catch (err: any) {
      console.error('Error loading available agents:', err)
      setError(err.response?.data?.error || 'Erro ao carregar agentes')
    } finally {
      setIsLoading(false)
    }
  }

  // Filter agents by search term
  const filteredAgents = availableAgents.filter(
    (agent) =>
      agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.tradingName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.document?.includes(searchTerm)
  )

  // Handle invite existing agent
  const handleInviteExisting = async () => {
    if (!selectedAgent) return

    setIsSending(true)
    setError(null)
    try {
      await inviteAgent({ freightForwarderId: selectedAgent })
      onSuccess()
      onClose()
    } catch (err: any) {
      console.error('Error sending invite:', err)
      setError(err.response?.data?.error || 'Erro ao enviar convite')
    } finally {
      setIsSending(false)
    }
  }

  // Handle invite by email
  const handleInviteByEmail = async () => {
    if (!emailForm.email || !emailForm.companyName) {
      setError('Preencha o email e o nome da empresa')
      return
    }

    setIsSending(true)
    setError(null)
    try {
      await inviteAgent({
        email: emailForm.email,
        companyName: emailForm.companyName,
        contactName: emailForm.contactName || undefined,
        phone: emailForm.phone || undefined,
      })
      onSuccess()
      onClose()
    } catch (err: any) {
      console.error('Error sending email invite:', err)
      setError(err.response?.data?.error || 'Erro ao enviar convite')
    } finally {
      setIsSending(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] min-h-[420px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">Convidar Agente</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 flex-shrink-0">
          <button
            onClick={() => setActiveTab('existing')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'existing'
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Buildings size={20} />
            Agentes do Tenant
          </button>
          <button
            onClick={() => setActiveTab('email')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'email'
                ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <EnvelopeSimple size={20} />
            Convidar por Email
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto overscroll-contain flex-1 min-h-0">
          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Existing Agents Tab */}
          {activeTab === 'existing' && (
            <div>
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlass
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Buscar agente..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Agents List */}
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size={32} className="animate-spin text-emerald-600" />
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="text-center py-8">
                  <Buildings size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">
                    {searchTerm
                      ? 'Nenhum agente encontrado'
                      : 'Nenhum agente disponivel para convite'}
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {!searchTerm &&
                      'Todos os agentes ja estao vinculados ou com convite pendente'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredAgents.map((agent) => (
                    <div
                      key={agent.id}
                      onClick={() => setSelectedAgent(agent.id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedAgent === agent.id
                          ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              selectedAgent === agent.id
                                ? 'bg-emerald-600 text-white'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <Buildings size={20} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{agent.name}</h4>
                            {agent.tradingName && (
                              <p className="text-sm text-gray-500">{agent.tradingName}</p>
                            )}
                            <p className="text-xs text-gray-400">{agent.document}</p>
                          </div>
                        </div>
                        {selectedAgent === agent.id && (
                          <CheckCircle
                            size={24}
                            weight="fill"
                            className="text-emerald-600"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500 mb-4">
                Envie um convite para um agente de carga que ainda nao esta cadastrado na
                plataforma.
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <EnvelopeSimple
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="email"
                    placeholder="email@empresa.com"
                    value={emailForm.email}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, email: e.target.value })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome da Empresa <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Buildings
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Nome da empresa"
                    value={emailForm.companyName}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, companyName: e.target.value })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Contato
                </label>
                <div className="relative">
                  <User
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Nome do responsavel"
                    value={emailForm.contactName}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, contactName: e.target.value })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone
                </label>
                <div className="relative">
                  <Phone
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="tel"
                    placeholder="+55 11 99999-9999"
                    value={emailForm.phone}
                    onChange={(e) =>
                      setEmailForm({ ...emailForm, phone: e.target.value })
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSending}>
            Cancelar
          </Button>
          <Button
            onClick={
              activeTab === 'existing' ? handleInviteExisting : handleInviteByEmail
            }
            disabled={
              isSending ||
              (activeTab === 'existing' && !selectedAgent) ||
              (activeTab === 'email' && (!emailForm.email || !emailForm.companyName))
            }
            className="gap-2"
            style={{ backgroundColor: 'rgb(5, 150, 105)' }}
          >
            {isSending ? (
              <>
                <Spinner size={20} className="animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <EnvelopeSimple size={20} />
                Enviar Convite
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
